// src/sections/UploadForm.js
import React, { useState } from "react";
import axios from "axios";

const DOC_TYPES = [
  "Ensayo",
  "Investigación",
  "Diagnóstico",
  "Revista",
  "Artículo",
  "Informe",
  "Tesis",
  "Libro",
  "Documento público",
];

const CATEGORY_OPTIONS = [
  "Economía",
  "Gestión pública",
  "Conflicto armado",
  "Equidad de género",
  "Mercado laboral",
  "Educación",
  "Ciencia y tecnología",
  "Salud",
  "Cultura",
  "Política",
  "Derechos humanos",
  "Normatividad",
];

export default function UploadForm({ token }) {
  const [form, setForm] = useState({
    title: "",
    tipoDocumento: "",
    descripcion: "",
    anoPublicacion: "",
    municipio: "",
    autores: "",
    entidad: "",
    categorias: "",      // AHORA ES STRING (una sola categoría)
    palabrasClave: "",
  });

  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState("");

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setMsg("");
  };

  const validate = () => {
    const newErrors = {};

    if (!form.title.trim()) newErrors.title = "El título es obligatorio.";
    if (!form.tipoDocumento)
      newErrors.tipoDocumento = "Selecciona el tipo de documento.";
    if (!form.descripcion.trim())
      newErrors.descripcion = "La descripción es obligatoria.";
    if (!form.anoPublicacion.trim())
      newErrors.anoPublicacion = "Indica el año de publicación.";
    if (!form.municipio.trim())
      newErrors.municipio = "El municipio es obligatorio.";
    if (!form.autores.trim())
      newErrors.autores = "Indica al menos un autor.";
    if (!form.entidad.trim())
      newErrors.entidad = "La entidad es obligatoria.";
    if (!form.categorias)
      newErrors.categorias = "Selecciona una categoría.";
    if (!form.palabrasClave.trim())
      newErrors.palabrasClave = "Agrega algunas palabras clave.";
    if (!file) newErrors.file = "Debes adjuntar un archivo PDF.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");

    if (!validate()) return;

    const fd = new FormData();
    fd.append("pdf", file);
    fd.append("title", form.title);
    fd.append("description", form.descripcion);
    fd.append("tipoDocumento", form.tipoDocumento);
    fd.append("anoPublicacion", form.anoPublicacion);
    fd.append("municipio", form.municipio);
    fd.append("autores", form.autores);
    fd.append("entidad", form.entidad);
    fd.append("categorias", form.categorias); // ahora es string
    fd.append("palabrasClave", form.palabrasClave);

    try {
      await axios.post("https://back.dataregion.com.co/api/documents/upload", fd, {
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "multipart/form-data",
        },
      });

      setMsg("✅ Documento subido con éxito.");
      setForm({
        title: "",
        tipoDocumento: "",
        descripcion: "",
        anoPublicacion: "",
        municipio: "",
        autores: "",
        entidad: "",
        categorias: "",
        palabrasClave: "",
      });
      setFile(null);
      setErrors({});
    } catch (err) {
      setMsg(err.response?.data?.message || "❌ Error al subir el documento.");
    }
  };

  return (
    <div className="institution-list">
      <h3>Subir documento</h3>
      {msg && <p className="small">{msg}</p>}

      <form className="institution-form" onSubmit={submit}>
        {/* Título */}
        <div className="input-group">
          <label>Título del documento</label>
          <input
            className={errors.title ? "error-input" : ""}
            placeholder="Título"
            value={form.title}
            onChange={(e) => handleChange("title", e.target.value)}
          />
          {errors.title && <p className="error-text">{errors.title}</p>}
        </div>

        {/* Tipo de documento */}
        <div className="input-group">
          <label>Tipo de documento</label>
          <select
            className={errors.tipoDocumento ? "error-input" : ""}
            value={form.tipoDocumento}
            onChange={(e) => handleChange("tipoDocumento", e.target.value)}
          >
            <option value="">Selecciona una opción</option>
            {DOC_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {errors.tipoDocumento && (
            <p className="error-text">{errors.tipoDocumento}</p>
          )}
        </div>

        {/* Descripción */}
        <div className="input-group">
          <label>Descripción breve</label>
          <textarea
            className={`${errors.descripcion ? "error-input" : ""} big-textarea`}
            placeholder="Breve descripción del documento"
            value={form.descripcion}
            onChange={(e) => handleChange("descripcion", e.target.value)}
          />
          {errors.descripcion && (
            <p className="error-text">{errors.descripcion}</p>
          )}
        </div>

        {/* Año + Municipio */}
        <div className="input-row">
          <div className="input-group">
            <label>Año de publicación</label>
            <input
              type="number"
              min="1900"
              max="2100"
              className={errors.anoPublicacion ? "error-input" : ""}
              placeholder="Ej. 2024"
              value={form.anoPublicacion}
              onChange={(e) => handleChange("anoPublicacion", e.target.value)}
            />
            {errors.anoPublicacion && (
              <p className="error-text">{errors.anoPublicacion}</p>
            )}
          </div>

          <div className="input-group">
            <label>Municipio</label>
            <input
              className={errors.municipio ? "error-input" : ""}
              placeholder="Municipio"
              value={form.municipio}
              onChange={(e) => handleChange("municipio", e.target.value)}
            />
            {errors.municipio && (
              <p className="error-text">{errors.municipio}</p>
            )}
          </div>
        </div>

        {/* Autores */}
        <div className="input-group">
          <label>Autores</label>
          <input
            className={errors.autores ? "error-input" : ""}
            placeholder="Separados por coma, ej: Nombre Apellido, Otro Autor"
            value={form.autores}
            onChange={(e) => handleChange("autores", e.target.value)}
          />
          {errors.autores && <p className="error-text">{errors.autores}</p>}
        </div>

        {/* Entidad */}
        <div className="input-group">
          <label>Entidad</label>
          <input
            className={errors.entidad ? "error-input" : ""}
            placeholder="Nombre de la entidad u organización"
            value={form.entidad}
            onChange={(e) => handleChange("entidad", e.target.value)}
          />
          {errors.entidad && <p className="error-text">{errors.entidad}</p>}
        </div>

        {/* Categorías – select simple */}
        <div className="input-group">
          <label>Categorías</label>
          <select
            className={errors.categorias ? "error-input" : ""}
            value={form.categorias}
            onChange={(e) => handleChange("categorias", e.target.value)}
          >
            <option value="">Selecciona una categoría</option>
            {CATEGORY_OPTIONS.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          {errors.categorias && (
            <p className="error-text">{errors.categorias}</p>
          )}
        </div>

        {/* Palabras clave */}
        <div className="input-group">
          <label>Palabras clave</label>
          <input
            className={errors.palabrasClave ? "error-input" : ""}
            placeholder="Ej: petróleo, empleo juvenil, conflicto armado"
            value={form.palabrasClave}
            onChange={(e) => handleChange("palabrasClave", e.target.value)}
          />
          {errors.palabrasClave && (
            <p className="error-text">{errors.palabrasClave}</p>
          )}
        </div>

        {/* Archivo PDF */}
        <div className="input-group">
          <label>Archivo PDF</label>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => {
              setFile(e.target.files[0]);
              setErrors((prev) => ({ ...prev, file: "" }));
              setMsg("");
            }}
          />
          {errors.file && <p className="error-text">{errors.file}</p>}
        </div>

        <button className="login-btn" type="submit">
          Subir PDF
        </button>
      </form>
    </div>
  );
}
